import type { MouseEvent, ReactNode } from "react"
import { Badge } from "@/components/ui/badge"
import { Circle, FileText, X } from "lucide-react"
import { testIds } from "@/lib/test-ids"
import { getDocumentIcon } from "@/lib/document-icons"
import type { DocumentReference } from "./chat-pane.types"

export interface ChatPaneActiveDocBadgeProps {
  /** `inline` is the current-doc chip row under the widget toolbar; `boxed` is the legacy card row. */
  variant?: 'boxed' | 'inline'
  activeDocumentId: string | undefined
  activeDocumentTitle?: string
  /** True when the user dismissed this active document for AI Context in the current conversation. */
  activeDocDismissed: boolean
  onDismiss: () => void
  references: DocumentReference[]
  onRemoveReference?: (documentId: string, ref?: DocumentReference) => void
  /** Click reference row (not x) - same navigation as sidebar */
  onReferenceOpen?: (ref: DocumentReference, event: MouseEvent) => void
  /** Workspace scope pip in a narrow column to the right of AI Context. */
  scopeSlot?: ReactNode
}

function ContextChips({
  showActiveDocBadge,
  activeDocumentTitle,
  onDismiss,
  references,
  onRemoveReference,
  onReferenceOpen,
}: {
  showActiveDocBadge: boolean
  activeDocumentTitle?: string
  onDismiss: () => void
  references: DocumentReference[]
  onRemoveReference?: (documentId: string, ref?: DocumentReference) => void
  onReferenceOpen?: (ref: DocumentReference, event: MouseEvent) => void
}) {
  return (
    <>
      {showActiveDocBadge && (
        <Badge
          variant="secondary"
          className="flex min-w-0 max-w-full items-center gap-1 text-[11px] pl-1.5 pr-1 py-0.5 bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20"
        >
          <Circle className="w-2 h-2 shrink-0 fill-green-500" />
          <FileText className="h-3 w-3 shrink-0" />
          <span className="min-w-0 truncate">
            {activeDocumentTitle || 'Active Document'}
          </span>
          <button
            type="button"
            onClick={onDismiss}
            className="ml-0.5 shrink-0 hover:bg-green-500/20 rounded-sm p-0.5"
            aria-label="Remove active document from context"
          >
            <X className="w-3 h-3" />
          </button>
        </Badge>
      )}
      {references.map((ref, idx) => {
        const refKey = `${ref.id}-${ref.lineStart ?? 'none'}-${ref.lineEnd ?? 'none'}-${idx}`
        const displayText = ref.lineStart !== undefined
          ? `${ref.title} (${ref.lineStart}${ref.lineEnd !== undefined && ref.lineEnd !== ref.lineStart ? `-${ref.lineEnd}` : ''})`
          : ref.title
        return (
          <Badge
            key={refKey}
            variant="outline"
            className="flex min-w-0 max-w-full items-center gap-1 text-[11px] pl-1.5 pr-1 py-0.5"
            data-testid={testIds.chatPane.reference(ref.id)}
          >
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-1 text-left cursor-pointer rounded-sm hover:bg-muted/60 pr-0.5"
              onClick={(e) => onReferenceOpen?.(ref, e)}
            >
              {getDocumentIcon(ref.type, "h-3 w-3 shrink-0")}
              <span className="min-w-0 truncate">{displayText}</span>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onRemoveReference?.(ref.id, ref)
              }}
              className="ml-0.5 hover:bg-muted rounded-sm p-0.5 shrink-0"
              aria-label={`Remove ${ref.title} from references`}
              data-testid={testIds.chatPane.referenceRemove(ref.id)}
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        )
      })}
    </>
  )
}

/**
 * Composer context row (boxed): AI Context badges (left) + optional workspace scope pip (right).
 *
 * Composer `inline`: current-doc chips only (workspace scope stays in the widget row).
 */
export function ChatPaneActiveDocBadge({
  variant = 'boxed',
  activeDocumentId,
  activeDocumentTitle,
  activeDocDismissed,
  onDismiss,
  references,
  onRemoveReference,
  onReferenceOpen,
  scopeSlot,
}: ChatPaneActiveDocBadgeProps) {
  const showActiveDocBadge = !!activeDocumentId && !activeDocDismissed
  const hasContextContent = showActiveDocBadge || references.length > 0

  if (!scopeSlot && !hasContextContent) return null

  if (variant === 'inline') {
    if (!hasContextContent) return null
    return (
      <div
        className="flex w-full min-w-0 flex-wrap items-center gap-1 px-2 pb-1.5 @[320px]/compose:px-3 @[320px]/compose:pb-2"
        data-testid={testIds.chatPane.composerExtrasRow}
      >
        <span className="sr-only">AI Context</span>
        <ContextChips
          showActiveDocBadge={showActiveDocBadge}
          activeDocumentTitle={activeDocumentTitle}
          onDismiss={onDismiss}
          references={references}
          onRemoveReference={onRemoveReference}
          onReferenceOpen={onReferenceOpen}
        />
      </div>
    )
  }

  return (
    <div
      className="@container mb-3 px-3 flex flex-col gap-2 @[280px]:flex-row @[280px]:items-stretch"
      data-testid={testIds.chatPane.composerContextRow}
    >
      <div className="flex-1 min-w-0 min-h-0 py-2 px-3 bg-muted/30 rounded-md border border-border/50">
        <div className="text-xs text-muted-foreground mb-1.5 font-medium">AI Context:</div>
        {hasContextContent ? (
          <div className="flex flex-wrap gap-1.5">
            <ContextChips
              showActiveDocBadge={showActiveDocBadge}
              activeDocumentTitle={activeDocumentTitle}
              onDismiss={onDismiss}
              references={references}
              onRemoveReference={onRemoveReference}
              onReferenceOpen={onReferenceOpen}
            />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground/50">
            Attach a document with @ or open a tab to add page context.
          </p>
        )}
      </div>

      {scopeSlot ? (
        <div
          className="flex w-full shrink-0 items-center justify-end @[280px]:w-auto @[280px]:self-center @[280px]:justify-start"
          data-testid={testIds.chatPane.composerScope}
        >
          {scopeSlot}
        </div>
      ) : null}
    </div>
  )
}
